import { UserStatus } from '../common';
import {
  AvailableFeature,
  IFeaturesResponse,
  IGetFeatureResponse,
} from '../features/types';

export interface IAccountConnection {
  company: string;
  side: string;
  username: string;
  date: Date;
  type: string;
  state: string;
  adverityConnectionId: number;
  id: string;
  isAuthorized: boolean;
}

export interface IGetAccountConnectionsResponseData {
  linkedAccounts: Array<IAccountConnection>;
  totalLinkedAccountsCount: number;
}

export interface INotApprovedUser {
  id: string;
  email: string;
  dateCreated: string;
}

export interface IGetNotApprovedUsersResponseData {
  customersCount: number;
  adminUnapprovedCustomersDto: Array<INotApprovedUser>;
}

export interface IChangeUserStatusRequestData {
  userId: string;
  userStatus: UserStatus;
}

export interface ICustomerReport {
  id: string;
  company: string;
  side: string;
  userName: string;
  databaseName: string;
  isSynapseSyncEnabled: boolean;
  reportingContainerName: string | null;
}

export interface IGetCustomerReportsResponseData {
  totalCount: number;
  customerReports: Array<ICustomerReport>;
}

export interface IDatastream {
  linkedAccountId: string;
  datastreamId: number;
  connectionId: number;
  datastreamTypeId: number;
  datastreamTemplateId: string;
  company: string;
  side: string;
  username: string;
  date: string;
  type: string;
  state: string;
}

export interface IGetDatastreamFetchStatusRequestData {
  DatastreamId: number;
  StartDate: string;
  EndDate?: string;
  MaxItems?: number;
}

export interface IGetDatastreamFetchStatusResponseData {
  count: number;
  datastreamExists: boolean;
  fetches: Array<{
    fetchStartDate: string;
    fetchEndDate: string;
    fetchStatus: string;
    manualFetch: boolean;
    id: string;
  }>;
}

export interface IGetDatastreamsResponseData {
  count: number;
  datastreams: Array<IDatastream>;
}

export interface IFetchDatastreamsDataRequestData {
  datastreamId: number | string;
  startDate: string;
  endDate: string;
}

export interface IFetchAccountConnectionDataRequestData {
  linkedAccountId: number | string;
  startDate: string;
  endDate: string;
}

export enum DatastreamFetchStatus {
  SUCCESS = 'Success',
  FAILED = 'Failed',
  NA = 'N/A',
}

export enum AdminType {
  USERS = 'users',
  ADVERITY_CONNECTIONS = 'adverity-connections',
  CUSTOMER_REPORTS = 'customer-reports',
  DATASTREAMS = 'datastreams',
  SPECIALIST_MARKETPLACE = 'specialist-marketplace',
  FEATURES = 'features',
  CUSTOM_REPORTS_PAGES = 'custom-reports-pages',
  DASHBOARD_V2_TESTING = 'dashboard-v2-testing',
  SIDE_USERS_MANAGEMENT = 'side-users-management',
}

export enum NotificationCenterType {
  EMAIL = 'Email',
  IN_APP = 'In-App',
}

export type IGetUsersResponseData = Array<{
  userFullName: string;
  userId: string;
  userType: string;
  userEmail: string;
  userStatus: UserStatus;
  signupDate: string;
  approvalDate: string;
}>;

export interface InviteSupplierMarketplaceResponse {
  invitationUrl: string;
  wasSuccessfull: boolean;
  errorMessage: string | null;
}

export interface IGetCustomersWithFeatures {
  id: string;
  email: string;
  userFullName: string;
  avatarUrl: string | null;
  features: Array<IGetFeatureResponse>;
}

export interface IGetCustomersWithFeaturesResponse {
  customersWithFeatures: Array<IGetCustomersWithFeatures>;
  totalCustomersCount: number;
}

export enum UpdateFeatureAction {
  ADD = 'add',
  DELETE = 'remove',
}

export interface IUpdadteFeatureRequest {
  featureName: AvailableFeature;
  action: UpdateFeatureAction;
}

export interface IUpdateCustomerWithFeatures {
  customerId: string;
  featureUpdates: Array<IUpdadteFeatureRequest>;
}

export interface IUpdateCustomersFeaturesRequest {
  customersWithFeatures: Array<IUpdateCustomerWithFeatures>;
}

export type IUpdateCustomersFeaturesResponse = IFeaturesResponse;

export interface ICustomPageResponse {
  displayName: string;
  pathName: string | null;
  url: string;
  id: string;
  analysisReportType: string;
}

export interface IGetCustomerWithPages {
  customerId: string;
  customerName: string;
  email: string;
  avatarUrl: string | null;
  customPages: Array<ICustomPageResponse>;
}
export interface ICustomPagesResponse {
  wasSuccessfull: boolean;
  errorMessage: string | null;
}
export interface IGetCustomersWithPagesResponse extends ICustomPagesResponse {
  customerWithPages: Array<IGetCustomerWithPages>;
}

export interface ICreateCustomPageRequest {
  customerId: string;
  name: string;
  pathName?: string;
  link: string;
  reportType: string;
}

export type ICreateCustomPageResponse = ICustomPagesResponse;

export interface IEditCustomPageRequest {
  pageId: string;
  name?: string;
  pathName?: string;
  link?: string;
}

export type IEditCustomPageResponse = ICustomPagesResponse;

export interface IToggleIsSynapseSyncEnabledRequest {
  customerId: string;
}

export interface IToggleIsSynapseSyncEnabledResponse {
  updatedIsSynapseSyncEnabled: boolean;
}

export interface ISetSynapseDatabaseRequest {
  customerId: string;
  databaseName: string;
}

export interface ISetSynapseDatabaseResponse {
  wasSuccessfull: boolean;
  errorMessage: string | null;
}

export interface IGetSynapseDatabaseConnectionRequest {
  databaseName: string;
}

export interface IGetSynapseDatabaseConnectionResponse {
  wasSuccessfull: boolean;
  errorMessage: string | null;
}

export interface IGetSynapseViewsRequest {
  databaseName: string;
}

export interface IGetSynapseViewsResponse {
  viewNames: Array<string>;
}

export interface ISyncSynapseDatabaseViewsRequest {
  customerId: string;
  databaseName: string;
  selectedViews: Array<string>;
  dateFrom?: string;
  dateTo?: string;
  historical: boolean;
}

export interface ISyncSynapseDatabaseViewsForAllCustomersRequest {
  selectedViews: Array<string>;
  dateFrom?: string;
  dateTo?: string;
  historical: boolean;
}

export interface ISyncSynapseDatabaseViewsResponse {
  wasSuccessfull: boolean;
  errorMessage: string | null;
}

export interface IGetClientsSynapseContainerLinkRequest {
  containerName: string;
}

export interface IGetClientsSynapseContainerLinkResponse {
  containerLink: string;
}

export interface IGetCustomPagesFromFolderRequest {
  customerId: string;
  folderLink: string;
  folderName: string;
}

export interface IGetCustomPagesFromFolderResponse {
  totalFiles: number;
  customPages: Array<ICustomPageFromFolder>;
}

export interface ICustomPageFromFolder {
  reportName?: string;
  pathName?: string;
  link?: string;
}

export interface ISideUserResponse {
  id: string;
  name: string;
  email: string;
}

export interface IGetCustomerWithSideUsers {
  customerId: string;
  customerName: string;
  email: string;
  avatarUrl: string | null;
  sideUsers: Array<ISideUserResponse>;
}

export interface IGetCustomersWithSideUsersResponse
  extends ICustomPagesResponse {
  customersWithSideUsers: Array<IGetCustomerWithSideUsers>;
}
